import React, { useEffect, useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
    MapPin, Star, Briefcase, Clock, Zap,
    Camera, Edit2, Plus, X, Check, Shield,
    FileText, ChevronRight, Upload,
} from 'lucide-react';
import ProfilePhotoEditor from './ProfilePhotoEditor';

/* ─── Helpers ─── */
function initials(user) {
    if (!user) return '?';
    const f = user.firstname || user.firstName || '';
    const l = user.lastname  || user.lastName  || '';
    return (f[0] || '') + (l[0] || '');
}

function avatarColor(str = '') {
    const PALETTE = ['#d97706', '#0f6f57', '#3b82f6', '#8b5cf6', '#f97316', '#0891b2'];
    let h = 0;
    for (let i = 0; i < str.length; i++) h = str.charCodeAt(i) + ((h << 5) - h);
    return PALETTE[Math.abs(h) % PALETTE.length];
}

/* ─── Sub-components ─── */

function VerifyRow({ icon: Icon, label, sub, ok, actionLabel, onAction }) {
    return (
        <div className={`pf-verify-row${ok ? ' is-ok' : ''}`}>
            <span className="pf-verify-icon"><Icon size={16} /></span>
            <div className="pf-verify-text">
                <span className="pf-verify-label">{label}</span>
                <span className="pf-verify-sub">{sub}</span>
            </div>
            {ok ? (
                <span className="pf-verify-ok mono"><Check size={12} /> Done</span>
            ) : (
                <button className="pf-btn-link" onClick={onAction}>{actionLabel}</button>
            )}
        </div>
    );
}

/* ─── Main component ─── */
const Profile = ({ user }) => {
    const [profileUser, setProfileUser] = useState(null);
    const [stats, setStats]             = useState(null);
    const [profilePhoto, setProfilePhoto] = useState(null);
    const [loading, setLoading]         = useState(true);
    const [error, setError]             = useState(null);

    // Bio editing
    const [editingBio, setEditingBio]   = useState(false);
    const [bioDraft, setBioDraft]       = useState('');
    const [bioSaving, setBioSaving]     = useState(false);

    // CV / Resume
    const [cvFile, setCvFile]           = useState(null);
    const [cvUploading, setCvUploading] = useState(false);
    const cvInputRef                    = useRef(null);

    // Photo editor
    const [isEditorOpen, setIsEditorOpen] = useState(false);

    // Delete account confirm
    const [showDelete, setShowDelete]   = useState(false);

    const navigate  = useNavigate();
    const token     = localStorage.getItem('accessToken') || sessionStorage.getItem('accessToken');
    const authHdr   = { Authorization: `Bearer ${token}` };

    /* ── Load user + stats ── */
    useEffect(() => {
        if (!user?.id) return;
        const load = async () => {
            try {
                const [uRes, sRes] = await Promise.all([
                    axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/users/id/${user.id}`, { headers: authHdr }),
                    axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/users/${user.id}/stats`, { headers: authHdr }),
                ]);
                setProfileUser(uRes.data);
                setStats(sRes.data);
                setBioDraft(uRes.data.bio || '');

                if (uRes.data.photoUrl?.includes('users/')) {
                    const sr = await axios.get(
                        `${process.env.REACT_APP_API_BASE_URL}/api/gcs/signed-url?objectName=${encodeURIComponent(uRes.data.photoUrl)}`,
                        { headers: authHdr }
                    );
                    setProfilePhoto(sr.data.signedUrl);
                } else {
                    setProfilePhoto(uRes.data.photoUrl);
                }
            } catch (e) {
                setError('Unable to load profile. Please refresh.');
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [user]);

    /* ── Save bio ── */
    const saveBio = async () => {
        setBioSaving(true);
        try {
            await axios.put(
                `${process.env.REACT_APP_API_BASE_URL}/api/users/update/${user.id}`,
                { bio: bioDraft },
                { headers: authHdr }
            );
            setProfileUser(u => ({ ...u, bio: bioDraft }));
            setEditingBio(false);
        } catch {
            alert('Failed to save. Please try again.');
        }
        setBioSaving(false);
    };

    /* ── Upload profile photo ── */
    const handleSaveProfilePhoto = async (croppedImageUrl, fileBlob) => {
        try {
            const formData = new FormData();
            formData.append('file', fileBlob, 'profile.jpg');
            await axios.post(
                `${process.env.REACT_APP_API_BASE_URL}/api/users/${user.username}/upload?type=photo`,
                formData,
                { headers: { ...authHdr, 'Content-Type': 'multipart/form-data' } }
            );
            const updated = await axios.get(
                `${process.env.REACT_APP_API_BASE_URL}/api/users/id/${user.id}`,
                { headers: authHdr }
            );
            setProfileUser(updated.data);
            if (updated.data.photoUrl?.includes('users/')) {
                const sr = await axios.get(
                    `${process.env.REACT_APP_API_BASE_URL}/api/gcs/signed-url?objectName=${encodeURIComponent(updated.data.photoUrl)}`,
                    { headers: authHdr }
                );
                setProfilePhoto(sr.data.signedUrl);
            } else {
                setProfilePhoto(updated.data.photoUrl);
            }
            setIsEditorOpen(false);
        } catch {
            alert('Failed to upload photo.');
        }
    };

    /* ── Upload CV ── */
    const handleCvUpload = async (file) => {
        if (!file) return;
        if (file.size > 5 * 1024 * 1024) { alert('File must be under 5 MB.'); return; }
        setCvUploading(true);
        try {
            const formData = new FormData();
            formData.append('file', file, file.name);
            await axios.post(
                `${process.env.REACT_APP_API_BASE_URL}/api/users/${user.username}/upload?type=cv`,
                formData,
                { headers: { ...authHdr, 'Content-Type': 'multipart/form-data' } }
            );
            setCvFile({ name: file.name, size: file.size });
        } catch {
            alert('Failed to upload CV.');
        }
        setCvUploading(false);
    };

    if (loading) return (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '80px', color: 'var(--ink-500)' }}>
            Loading profile…
        </div>
    );
    if (error) return (
        <div style={{ textAlign: 'center', padding: '80px', color: '#b91c1c' }}>{error}</div>
    );
    if (!profileUser) return null;

    const name = `${profileUser.firstname || ''} ${profileUser.lastname || ''}`.trim();
    const ini  = initials(profileUser);
    const bg   = avatarColor(name);
    const hasCv = cvFile || profileUser.cvUrl;

    return (
        <div className="pf-page">
            <main className="pf-main">
                <div className="ix-container" style={{ maxWidth: '1100px' }}>

                    {/* ── Hero ── */}
                    <section className="pf-hero">
                        <div className="pf-hero-bg" />
                        <div className="pf-hero-row">

                            {/* Avatar */}
                            <button
                                className="pf-avatar"
                                onClick={() => setIsEditorOpen(true)}
                                aria-label="Change profile picture"
                            >
                                {profilePhoto ? (
                                    <img src={profilePhoto} alt={name} />
                                ) : (
                                    <span className="pf-avatar-initials" style={{ background: bg, color: '#fff' }}>
                                        {ini.toUpperCase()}
                                    </span>
                                )}
                                <span className="pf-avatar-cam"><Camera size={14} /></span>
                            </button>

                            {/* Name + meta */}
                            <div className="pf-hero-name">
                                <div className="pf-hero-line">
                                    <h1 className="pf-name">{name || 'Your Name'}</h1>
                                    {stats?.idVerified && (
                                        <span className="pf-verified-pill mono">
                                            <Check size={11} /> Verified
                                        </span>
                                    )}
                                </div>
                                <div className="pf-hero-meta">
                                    <span><MapPin size={14} /> {profileUser.location || 'Location not set'}</span>
                                    <span className="pf-dot">·</span>
                                    <span className="mono">{profileUser.email}</span>
                                </div>
                            </div>

                            <Link
                                to={`/profile/${user.id}/personal-info`}
                                className="ix-btn ix-btn-outline ix-btn-sm"
                                style={{ marginLeft: 'auto', flexShrink: 0 }}
                            >
                                <Edit2 size={13} /> Edit profile
                            </Link>
                        </div>

                        {/* Stats strip */}
                        <div className="pf-stats">
                            <div className="pf-stat is-accent">
                                <div className="pf-stat-label">Rating</div>
                                <div className="pf-stat-value">
                                    <Star size={16} /> {stats?.rating?.toFixed(1) ?? '—'}
                                </div>
                                <div className="pf-stat-sub">{stats?.totalReviews ?? 0} reviews</div>
                            </div>
                            <div className="pf-stat">
                                <div className="pf-stat-label">Jobs done</div>
                                <div className="pf-stat-value">
                                    <Briefcase size={16} /> {stats?.jobsDone ?? 0}
                                </div>
                                <div className="pf-stat-sub">completed</div>
                            </div>
                            <div className="pf-stat">
                                <div className="pf-stat-label">Response time</div>
                                <div className="pf-stat-value">
                                    <Clock size={16} /> {stats?.responseTime ?? '—'}
                                </div>
                                <div className="pf-stat-sub">median</div>
                            </div>
                            <div className="pf-stat">
                                <div className="pf-stat-label">Jobs posted</div>
                                <div className="pf-stat-value">
                                    <Zap size={16} /> {stats?.jobsPosted ?? 0}
                                </div>
                                <div className="pf-stat-sub">as poster</div>
                            </div>
                        </div>
                    </section>

                    {/* ── Two-column body ── */}
                    <div className="pf-grid">

                        {/* Left: Bio + CV */}
                        <div className="pf-col-main">
                            <section className="pf-section">
                                <header className="pf-section-head">
                                    <h2 className="pf-section-title">About me</h2>
                                    {!editingBio && (
                                        <button
                                            className="pf-btn-link"
                                            onClick={() => { setBioDraft(profileUser.bio || ''); setEditingBio(true); }}
                                        >
                                            <Edit2 size={12} /> Edit
                                        </button>
                                    )}
                                </header>

                                {editingBio ? (
                                    <div className="pf-bio-edit">
                                        <textarea
                                            value={bioDraft}
                                            onChange={e => setBioDraft(e.target.value)}
                                            placeholder="Tell posters a bit about yourself — what you're good at, your work style, anything that helps them say yes."
                                            maxLength={600}
                                            rows={5}
                                            autoFocus
                                        />
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '6px' }}>
                                            <span className="mono" style={{ fontSize: '11px', color: 'var(--ink-500)' }}>
                                                {bioDraft.length} / 600
                                            </span>
                                            <div className="pf-bio-actions">
                                                <button className="ix-btn ix-btn-ghost ix-btn-sm" onClick={() => setEditingBio(false)}>Cancel</button>
                                                <button
                                                    className="ix-btn ix-btn-primary ix-btn-sm"
                                                    onClick={saveBio}
                                                    disabled={bioSaving}
                                                >
                                                    {bioSaving ? 'Saving…' : 'Save'}
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <p className="pf-bio">
                                        {profileUser.bio || (
                                            <span style={{ color: 'var(--ink-400)', fontStyle: 'italic' }}>
                                                No bio yet. Click Edit to add one.
                                            </span>
                                        )}
                                    </p>
                                )}

                                {/* CV / Resume upload */}
                                <div className="pf-resume-block">
                                    <div className="pf-mini-label">Resume / CV</div>

                                    {hasCv ? (
                                        <div className="pf-resume-file">
                                            <span className="pf-resume-icon"><FileText size={16} /></span>
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                <div className="pf-resume-name">
                                                    {cvFile?.name || 'CV uploaded'}
                                                </div>
                                                <div className="pf-resume-sub">
                                                    {cvFile
                                                        ? `${(cvFile.size / 1024).toFixed(0)} KB · uploaded just now`
                                                        : 'On file'}
                                                </div>
                                            </div>
                                            <button
                                                className="pf-btn-link"
                                                onClick={() => cvInputRef.current?.click()}
                                            >
                                                Replace
                                            </button>
                                            <button
                                                className="pf-btn-link"
                                                style={{ color: '#b91c1c' }}
                                                onClick={() => setCvFile(null)}
                                            >
                                                <X size={12} />
                                            </button>
                                        </div>
                                    ) : (
                                        <label
                                            className="pf-resume-drop"
                                            onDragOver={e => e.preventDefault()}
                                            onDrop={e => {
                                                e.preventDefault();
                                                const f = e.dataTransfer.files?.[0];
                                                if (f) handleCvUpload(f);
                                            }}
                                        >
                                            <span className="pf-resume-drop-icon">
                                                {cvUploading ? <Clock size={18} /> : <Upload size={18} />}
                                            </span>
                                            <span className="pf-resume-drop-title">
                                                {cvUploading ? 'Uploading…' : 'Upload your CV'}
                                            </span>
                                            <span className="pf-resume-drop-sub">
                                                Drag and drop or click — PDF, DOC, DOCX up to 5 MB
                                            </span>
                                            <input
                                                ref={cvInputRef}
                                                type="file"
                                                accept=".pdf,.doc,.docx"
                                                hidden
                                                onChange={e => {
                                                    const f = e.target.files?.[0];
                                                    if (f) handleCvUpload(f);
                                                }}
                                            />
                                        </label>
                                    )}
                                    <input
                                        ref={cvInputRef}
                                        type="file"
                                        accept=".pdf,.doc,.docx"
                                        hidden
                                        onChange={e => {
                                            const f = e.target.files?.[0];
                                            if (f) handleCvUpload(f);
                                        }}
                                    />
                                </div>
                            </section>
                        </div>

                        {/* Right: Trust + Account */}
                        <div className="pf-col-side">

                            {/* Trust & Verification */}
                            <section className="pf-section">
                                <header className="pf-section-head">
                                    <div>
                                        <h2 className="pf-section-title">Trust & verification</h2>
                                        <p className="pf-section-sub">Verified profiles get picked twice as often.</p>
                                    </div>
                                </header>
                                <div className="pf-verify">
                                    <VerifyRow
                                        icon={Shield}
                                        label="Government ID"
                                        sub="Passport or driving licence"
                                        ok={stats?.idVerified}
                                        actionLabel="Verify ID"
                                        onAction={() => navigate(`/profile/${user.id}/personal-info`)}
                                    />
                                    <VerifyRow
                                        icon={Check}
                                        label="Email"
                                        sub={profileUser.email || '—'}
                                        ok={stats?.emailVerified}
                                        actionLabel="Verify email"
                                        onAction={() => {}}
                                    />
                                    <VerifyRow
                                        icon={Check}
                                        label="Phone"
                                        sub={profileUser.phoneNumber && profileUser.phoneNumber !== '+44'
                                            ? profileUser.phoneNumber : 'Not added yet'}
                                        ok={stats?.phoneVerified}
                                        actionLabel="Add phone"
                                        onAction={() => navigate(`/profile/${user.id}/personal-info`)}
                                    />
                                </div>
                            </section>

                            {/* Account */}
                            <section className="pf-section">
                                <header className="pf-section-head">
                                    <h2 className="pf-section-title">Account</h2>
                                </header>
                                <ul className="pf-settings">
                                    {/* Delete account */}
                                    <li>
                                        <button
                                            className="pf-setting pf-setting-danger"
                                            onClick={() => setShowDelete(d => !d)}
                                        >
                                            <div>
                                                <div className="pf-setting-l">Delete account</div>
                                                <div className="pf-setting-sub">Permanent, cannot be undone</div>
                                            </div>
                                            <ChevronRight size={16} className="pf-setting-chev" />
                                        </button>
                                        {showDelete && (
                                            <div className="pf-delete-dialog">
                                                <p>This will permanently delete your account, listings, and applications. Are you sure?</p>
                                                <div className="pf-delete-actions">
                                                    <button className="ix-btn ix-btn-ghost ix-btn-sm" onClick={() => setShowDelete(false)}>
                                                        Cancel
                                                    </button>
                                                    <button
                                                        className="ix-btn ix-btn-sm"
                                                        style={{ background: '#ef4444', color: '#fff' }}
                                                        onClick={async () => {
                                                            try {
                                                                const token = localStorage.getItem('accessToken') || sessionStorage.getItem('accessToken');
                                                                await axios.delete(
                                                                    `${process.env.REACT_APP_API_BASE_URL}/api/users/${user.id}`,
                                                                    { headers: { Authorization: `Bearer ${token}` } }
                                                                );
                                                                sessionStorage.clear();
                                                                window.location.href = '/';
                                                            } catch (e) {
                                                                alert('Failed to delete account. Please try again.');
                                                            }
                                                        }}
                                                    >
                                                        Yes, delete my account
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </li>
                                </ul>
                            </section>
                        </div>
                    </div>
                </div>
            </main>

            {/* Photo editor modal */}
            {isEditorOpen && (
                <ProfilePhotoEditor
                    onClose={() => setIsEditorOpen(false)}
                    onSave={handleSaveProfilePhoto}
                />
            )}
        </div>
    );
};

export default Profile;
